import styled, { css } from "styled-components";
import { Link } from "wouter";

import { BaseButton } from "../../../../../components/Button";
import { IconButton } from "../../../../../components/IconButton";
import { inlineLinkStyle } from "../../../../../components/SettlementLock/styles";
import {
  FONTS,
  OVERLAYS,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  TOUCH_TARGET,
} from "../../../../../utils/constants";

const SEARCH_WIDTH = 260;
const DETAILS_MAX_WIDTH = 520;

const focusRing = css`
  outline: 0;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 4px var(--shadow-focus);
`;

const amountColor = ($tone: "positive" | "negative" | "neutral") => {
  if ($tone === "positive") {
    return "var(--color-success)";
  }
  if ($tone === "negative") {
    return "var(--color-text-default)";
  }
  return "var(--color-text-secondary)";
};

export const TableCard = styled.section`
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  box-shadow: var(--shadow-small);
  font-family: ${FONTS.serif};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;
  margin: ${SPACING_RESPONSIVE.mobile.sectionGap}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
    margin: ${SPACING_RESPONSIVE.tablet.sectionGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
    margin: ${SPACING_RESPONSIVE.desktop.sectionGap}px;
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const Title = styled.h2`
  margin: 0;
  font-family: ${FONTS.serif};
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-default);
`;

export const Subtitle = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--text-base);
`;

/* -------------------------------- Toolbar -------------------------------- */

export const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
  }
`;

export const SearchGroup = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-start;
  gap: ${SPACING.xs}px;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    align-items: center;
    gap: ${SPACING.md}px;
  }
`;

export const SearchField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  @media ${QUERIES.tabletAndUp} {
    width: ${SEARCH_WIDTH}px;
  }
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: ${SPACING.sm + SPACING.xxs}px;
  display: inline-flex;
  color: var(--color-text-muted);
  pointer-events: none;

  & > svg {
    font-size: var(--text-md);
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  min-height: ${TOUCH_TARGET}px;
  padding: ${SPACING.xs + SPACING.xxs}px ${SPACING.xl}px
    ${SPACING.xs + SPACING.xxs}px ${SPACING.xl + SPACING.xxs}px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background-0);
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-md);

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    ${focusRing}
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
`;

export const SearchClearButton = styled(IconButton)`
  position: absolute;
  right: ${SPACING.xxs}px;
  color: var(--color-text-muted);
  font-size: var(--text-md);
`;

/* ------------------------------ Desktop table ----------------------------- */

export const TableScroll = styled.div`
  margin-inline: -${SPACING.sm}px;
`;

export const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

export const HeaderCell = styled.th<{ $width: string; $isNumeric?: boolean }>`
  width: ${({ $width }) => $width};
  padding: ${SPACING.sm}px;
  border-bottom: 1px solid var(--color-card-border);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: ${({ $isNumeric }) => ($isNumeric ? "right" : "left")};
  text-transform: uppercase;
`;

export const HiddenLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const Row = styled.tr`
  border-bottom: 1px solid var(--color-card-border);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--hover-background);
  }
`;

export const Cell = styled.td<{ $isNumeric?: boolean }>`
  padding: ${SPACING.sm + SPACING.xs}px ${SPACING.sm}px;
  vertical-align: middle;
  text-align: ${({ $isNumeric }) => ($isNumeric ? "right" : "left")};
  color: var(--color-text-default);
  font-size: var(--text-md);
  font-variant-numeric: tabular-nums;
`;

export const DateCell = styled(Cell)`
  color: var(--color-text-secondary);
  font-size: var(--text-base);
  white-space: nowrap;
`;

export const ActionsCell = styled(Cell)`
  padding-block: ${SPACING.xxs}px;
  text-align: right;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const TypeButton = styled(BaseButton)`
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-md);
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const Secondary = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
`;

export const DetailText = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--text-base);
  overflow-wrap: anywhere;
`;

export const PaidToLink = styled(Link)`
  ${inlineLinkStyle}
`;

export const PaidToLine = styled(Secondary)`
  display: block;
  font-weight: 400;
`;

export const Amount = styled.span<{
  $tone: "positive" | "negative" | "neutral";
}>`
  color: ${({ $tone }) => amountColor($tone)};
  font-weight: 600;
  white-space: nowrap;
`;

export const Unchanged = styled.span`
  color: var(--color-text-muted);
`;

export const EarnedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xxs}px;
  color: var(--color-success);
  font-size: var(--text-sm);

  svg {
    font-size: var(--text-md);
  }
`;

export const LockNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);

  svg {
    font-size: var(--text-md);
  }
`;

/* ------------------------------ Mobile rows ------------------------------ */

export const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  margin-inline: -${SPACING.sm}px;
`;

export const MobileRow = styled(BaseButton)`
  display: grid;
  gap: ${SPACING.xxs}px;
  width: 100%;
  min-height: ${TOUCH_TARGET}px;
  padding: ${SPACING.sm + SPACING.xs}px ${SPACING.sm}px;
  border: 0;
  border-top: 1px solid var(--color-card-border);
  background: none;
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
  }
`;

export const MobileLine = styled.span<{ $isSub?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${SPACING.sm}px;
  color: ${({ $isSub }) =>
    $isSub ? "var(--color-text-secondary)" : "var(--color-text-default)"};
  font-size: ${({ $isSub }) => ($isSub ? "var(--text-sm)" : "var(--text-md)")};
  font-variant-numeric: tabular-nums;
`;

export const MobileTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* ------------------------------ Empty & legend ------------------------------ */

export const EmptyResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.xl}px ${SPACING.md}px;
  color: var(--color-text-secondary);
  font-size: var(--text-md);
  text-align: center;
`;

export const EmptyText = styled.p`
  margin: 0;
`;

export const Legend = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
`;

/* --------------------------------- Pager --------------------------------- */

export const Pager = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
  }
`;

export const PagerGrow = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PageSizeControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  white-space: nowrap;
`;

/* -------------------------------- Details -------------------------------- */

export const DetailsBackdrop = styled.div<{ $isSheet: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: ${({ $isSheet }) => ($isSheet ? "flex-end" : "center")};
  justify-content: center;
  padding: ${({ $isSheet }) => ($isSheet ? 0 : SPACING.lg)}px;
  background: ${OVERLAYS.backdropLight};
`;

export const DetailsDialog = styled.div<{ $isSheet: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  width: 100%;
  max-width: ${({ $isSheet }) => ($isSheet ? "none" : `${DETAILS_MAX_WIDTH}px`)};
  max-height: 90vh;
  overflow-y: auto;
  padding: ${SPACING.lg}px;
  padding-bottom: ${({ $isSheet }) =>
    $isSheet
      ? `max(${SPACING.lg}px, env(safe-area-inset-bottom))`
      : `${SPACING.lg}px`};
  border: 1px solid var(--color-border);
  border-radius: ${({ $isSheet }) => ($isSheet ? "12px 12px 0 0" : "12px")};
  background: var(--color-background-2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  font-family: ${FONTS.serif};
  color: var(--color-text-default);

  &:focus {
    outline: none;
  }
`;

export const DetailsHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${SPACING.md}px;
`;

export const Eyebrow = styled.span`
  display: block;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
`;

export const DetailsTitle = styled.h2`
  margin: ${SPACING.xxs}px 0 0;
  font-family: ${FONTS.serif};
  font-size: var(--text-xl);
  font-weight: 700;
`;

export const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.sm}px ${SPACING.md}px;
  margin: 0;
`;

export const DetailItem = styled.div`
  min-width: 0;
`;

export const DetailTerm = styled.dt`
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
`;

export const DetailValue = styled.dd`
  margin: ${SPACING.xxs}px 0 0;
  font-size: var(--text-md);
  font-weight: 600;
  overflow-wrap: anywhere;
`;

export const Explanation = styled.p`
  margin: 0;
  padding-top: ${SPACING.sm}px;
  border-top: 1px solid var(--color-card-border);
  color: var(--color-text-secondary);
  font-size: var(--text-base);
`;

export const DetailsActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${SPACING.sm}px;

  & > *:first-child {
    margin-right: auto;
  }
`;
